import { Modal } from 'antd';
import { useSelector } from 'react-redux';
import {
  parseImageUrlToFileItem,
  defaultDateFormat,
  displayValueUnit,
} from '../../../lib/common.helpers';
import { Button, CloseIcon, Input, Subtitle } from '../../shared';
import { customFieldsValues } from '../../../entities/util-functions';
import { IMusselSeeding } from '../../../entities/growing.entities';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate, transUnit } from '../../../lib/lang.helper';
import {
  SelectFarmsByFarmID,
  SelectSeedingFields,
} from '../../../store/extra/extra.selector';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import { selectAccount } from '../../../store/auth/auth.selector';
import { SeedSizeRange } from '../../CommonViews';

interface Props {
  visible: boolean;
  seeding: IMusselSeeding;
  onClose: () => void;
}

const MusselSeedViewModal = ({ visible, seeding, onClose }: Props) => {
  const lang = useSelector(selectLang);
  const farmsData = SelectFarmsByFarmID(seeding.line.farm_id);
  const farm = farmsData.find(x => x.id === seeding.line.farm_id);
  const unitType = useSelector(selectAccount)?.display_unit;

  const customFields = SelectSeedingFields(seeding.line.farm_id);
  const customValues = customFieldsValues(customFields, seeding.custom_values);

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      closable
      closeIcon={<CloseIcon />}
      width={600}
      footer={null}
    >
      <div className='wrap view-modal'>
        <div className='d-flex align-center mb-16'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Seeding Details')}:
          </Subtitle>
          <Subtitle
            className='ml-7'
            color='black-3'
            align='left'
            size={1}
            fontWeight={500}
          >
            {`${farm?.name} - ${seeding.line.line_name}`}
          </Subtitle>
        </div>
        <div className='pt-16 pb-24'>
          <div className='d-flex'>
            <div className='info-card mr-17'>
              <div className='info-label'>{translate(lang, 'Season')}</div>
              <div className='info-value'>{seeding.season_name}</div>
            </div>
            <div className='info-card ml-17'>
              <div className='info-label'>
                {translate(lang, 'Seed line length')}
              </div>
              <div className='info-value'>
                {displayValueUnit(seeding.line_length, unitType, 'distance_m')}
              </div>
            </div>
          </div>
          <div className='d-flex'>
            <div className='info-card mr-17'>
              <div className='info-label'>
                {transUnit(lang, 'line_seed_date', farm?.structure)}
              </div>
              <div className='info-value'>
                {defaultDateFormat(seeding.planned_date_seed)}
              </div>
            </div>
            <div className='info-card ml-17'>
              <div className='info-label'>
                {translate(lang, 'Planned harvest date')}
              </div>
              <div className='info-value'>
                {defaultDateFormat(seeding.planned_date_harvest)}
              </div>
            </div>
          </div>
          <div className='d-flex'>
            <div className='info-card mr-17'>
              <div className='info-label'>{translate(lang, 'Spat source')}</div>
              <div className='info-value'>
                {!!seeding.is_catch_spat
                  ? 'Catch Spat'
                  : seeding.spat_storage
                  ? `${seeding.spat_storage.source} - ${displayValueUnit(
                      seeding.spat_amount,
                      unitType,
                      'weight_kg',
                    )}`
                  : '-'}
              </div>
            </div>
            <div className='info-card ml-17'>
              <div className='info-label'>{translate(lang, 'Density')}</div>
              <div className='info-value'>
                {!seeding.density_max
                  ? seeding.density
                  : `${seeding.density} - ${seeding.density_max}`}
              </div>
            </div>
          </div>
          <div className='d-flex'>
            <div className='info-card mr-17'>
              <div className='info-label'>{translate(lang, 'Spat size')}</div>
              <div className='info-value'>
                <SeedSizeRange data={seeding} />
              </div>
            </div>
            <div className='info-card ml-17'>
              <div className='info-label'>{translate(lang, 'Drop')}</div>
              <div className='info-value'>
                {displayValueUnit(seeding.drop, unitType, 'distance_m')}
              </div>
            </div>
          </div>
          <div className='d-flex'>
            <div className='info-card mr-17'>
              <div className='info-label'>{translate(lang, 'Submersion')}</div>
              <div className='info-value'>
                {displayValueUnit(seeding.submersion, unitType, 'distance_m')}
              </div>
            </div>
            <div className='info-card ml-17'>
              <div className='info-label'>{translate(lang, 'Spacing')}</div>
              <div className='info-value'>
                {displayValueUnit(seeding.spacing, unitType, 'size_mm')}
              </div>
            </div>
          </div>
          <div className='info-card'></div>
          {customValues?.map(x => (
            <div key={x.id} className='info-card'>
              <div className='info-label'>{x.label}</div>
              <div className='info-value'>
                {x.value === undefined || x.value === null
                  ? ''
                  : `${x.value} ${x.unit ?? ''}`}
              </div>
            </div>
          ))}
          <div className='mt-7 mb-7'>
            <Input
              label={translate(lang, 'Comment')}
              type='textarea'
              value={seeding.comment ?? ''}
              disabled={true}
            />
          </div>
          {!!seeding.image_pdf_files && seeding.image_pdf_files.length > 0 && (
            <div className='mt-7 mb-7'>
              <FilesWall
                label={translate(lang, 'Images')}
                fileList={
                  seeding.image_pdf_files.map(x =>
                    parseImageUrlToFileItem(x),
                  ) as any
                }
                disabled={true}
              />
            </div>
          )}
        </div>
        <div className='modal-button d-flex justify-end align-center'>
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={onClose}
          >
            {translate(lang, 'Close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MusselSeedViewModal;
